import { PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useMemo } from 'react';

import { useFilters } from '../../hooks/filters';
import { useLivescoresStore } from '../../store';
import { track } from '../../utils/tracking';

import type { FilterState } from './ModalContent';
import ModalContent from './ModalContent';

import { useCountry } from '@hooks/useCountry';
import FilterModal from '@shared/FilterModal';
import { useTranslateFn } from '@shared/Translate';

import * as classes from './styles.module.scss';

const ButtonContent = memo(() => {
  const t = useTranslateFn();
  return (
    <>
      {t('refine')}
      <PkIcon className='pk-ml--xs' name='actions-filter' size={24} />
    </>
  );
});
ButtonContent.displayName = 'ButtonContent';

const CompetitionFilter = () => {
  const t = useTranslateFn();
  const country = useLivescoresStore((state) => state.broadcastCountry);
  const setCountry = useLivescoresStore((state) => state.setBroadcastCountry);
  const { filters, setFilters } = useFilters();

  const userCountry = useCountry();

  const defaultState: FilterState = useMemo(
    () => ({
      country: userCountry,
      competition: null,
    }),
    [userCountry],
  );

  const currentFilters: FilterState = useMemo(
    () => ({
      country: country ?? userCountry,
      competition: filters.competition,
    }),
    [country, userCountry, filters.competition],
  );

  const handleModalOpen = () => {
    track('Refine', 'uefa_calendar_refine');
  };

  const trackFilter = (action: string, name: string) => track(action, name, action);

  const handleModalCloseClick = () => {
    trackFilter('Close filters', 'uefa_calendar_close_filter');
  };

  const handleResetFilters = () => {
    trackFilter('Reset filters', 'uefa_calendar_reset_filter');
  };

  const trackApplyFilters = ({ country, competition }: FilterState) => {
    const countryCode = country?.regionCodeShort ?? country?.countryCode;
    const appliedFilters = [countryCode, competition?.code].filter(Boolean);

    if (!appliedFilters.length) return;

    trackFilter(
      `Select ${appliedFilters.length} filters - ${appliedFilters.join('; ')}`,
      'uefa_calendar_select_filter',
    );
  };

  const onUpdate = (newFilters: FilterState) => {
    const { country: newCountry, competition } = newFilters;
    trackApplyFilters(newFilters);

    setCountry(newCountry);

    if (competition?.id === currentFilters.competition?.id && competition?.phase === currentFilters.competition?.phase)
      return;

    window.scrollTo({ top: 100, behavior: 'instant' });

    setFilters({
      competition: competition?.id ? competition : null,
    });
  };

  return (
    <FilterModal
      initialState={currentFilters}
      resetState={defaultState}
      onUpdate={onUpdate}
      onModalOpen={handleModalOpen}
      onModalCloseClick={handleModalCloseClick}
      onResetFilters={handleResetFilters}
      headerText={t('refine')}
      buttonClassName={clsx(classes.button, 'pk-w--100', 'pk-w-sm--auto')}
      buttonWidth='auto'
      ButtonContent={ButtonContent}
      Content={ModalContent}
    />
  );
};

export default memo(CompetitionFilter);
